import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Loader2, CheckCircle2, AlertCircle, AlertTriangle, X } from "lucide-react";
import { useIngestJobs } from "@/hooks/use-ingest-jobs";
import {
  IngestJob,
  IngestJobTone,
  describeIngestJob,
  isJobActive,
} from "@/services/ingest-jobs.service";
import { cn } from "@/lib/utils";

const RECENT_TERMINAL_MS = 2 * 60 * 1000; // keep a finished import visible briefly

const TONE_STYLES: Record<
  IngestJobTone,
  { card: string; bar: string; dot: string; text: string }
> = {
  info: {
    card: "border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30",
    bar: "bg-gradient-to-r from-pink-500 to-purple-500",
    dot: "bg-blue-500",
    text: "text-blue-900 dark:text-blue-100",
  },
  success: {
    card: "border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30",
    bar: "bg-green-500",
    dot: "bg-green-500",
    text: "text-green-900 dark:text-green-100",
  },
  warning: {
    card: "border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30",
    bar: "bg-amber-500",
    dot: "bg-amber-500",
    text: "text-amber-900 dark:text-amber-100",
  },
  error: {
    card: "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30",
    bar: "bg-red-500",
    dot: "bg-red-500",
    text: "text-red-900 dark:text-red-100",
  },
};

function ToneIcon({ tone }: { tone: IngestJobTone }) {
  if (tone === "success") return <CheckCircle2 className="h-4 w-4 text-green-600" />;
  if (tone === "warning") return <AlertTriangle className="h-4 w-4 text-amber-600" />;
  if (tone === "error") return <AlertCircle className="h-4 w-4 text-red-600" />;
  return <Loader2 className="h-4 w-4 animate-spin text-blue-600" />;
}

function shortJobId(jobId: string) {
  return jobId.length > 8 ? jobId.slice(0, 8) : jobId;
}

function JobBanner({
  job,
  jobs,
  onDismiss,
  onSelect,
}: {
  job: IngestJob;
  jobs: IngestJob[];
  onDismiss?: () => void;
  onSelect: (jobId: string) => void;
}) {
  const display = describeIngestJob(job);
  const styles = TONE_STYLES[display.tone];
  const active = isJobActive(job);
  const showStalled = active && job.stalled;

  return (
    <Card className={cn("border", styles.card)}>
      <CardContent className="space-y-2 p-4">
        {jobs.length > 1 ? (
          <div className="flex min-w-0 flex-col gap-2 border-b border-foreground/10 pb-3 sm:flex-row sm:items-center">
            <Badge
              variant="outline"
              className="w-fit shrink-0 border-foreground/15 bg-background/70 text-foreground"
            >
              {jobs.length} import tasks
            </Badge>

            <div
              role="group"
              aria-label="Select an import task"
              className="flex min-w-0 gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {jobs.map((candidate) => {
                const candidateDisplay = describeIngestJob(candidate);
                const selected = candidate.id === job.id;

                return (
                  <button
                    key={candidate.id}
                    type="button"
                    aria-pressed={selected}
                    aria-label={`View import job ${candidate.id}, ${candidateDisplay.label}`}
                    title={candidate.id}
                    onClick={() => onSelect(candidate.id)}
                    className={cn(
                      "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border px-2.5 font-mono text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      selected
                        ? "border-foreground/20 bg-background text-foreground shadow-sm"
                        : "border-transparent bg-background/40 text-muted-foreground hover:border-foreground/10 hover:bg-background/70 hover:text-foreground",
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        TONE_STYLES[candidateDisplay.tone].dot,
                      )}
                    />
                    Job {shortJobId(candidate.id)}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <ToneIcon tone={display.tone} />
            <span className={cn("text-sm font-medium", styles.text)}>{display.label}</span>
            {job.fileName && (
              <span className="min-w-0 truncate text-xs text-muted-foreground">
                · {job.fileName}
              </span>
            )}
          </div>
          {!active && onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <span>Job ID</span>
          <code className="break-all font-mono text-foreground/80">{job.id}</code>
        </div>

        {display.percent != null ? (
          <Progress value={display.percent} className="h-2" indicatorClassName={styles.bar} />
        ) : (
          active && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Working…</span>
            </div>
          )
        )}

        {display.detail && <p className="text-sm text-muted-foreground">{display.detail}</p>}

        {showStalled && (
          <p className="text-xs text-amber-700 dark:text-amber-300">
            No recent progress — this import may have stalled.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export function ImportProgressBanner({ catalogueId }: { catalogueId: string }) {
  const { jobs } = useIngestJobs(catalogueId);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const visible = useMemo(() => {
    const now = Date.now();

    return jobs.filter((job) => {
      if (dismissed.has(job.id)) return false;
      if (isJobActive(job)) return true;
      const finishedAt = (job.completedAt ?? job.updatedAt)?.getTime() ?? Number.NaN;
      return Number.isFinite(finishedAt) && now - finishedAt < RECENT_TERMINAL_MS;
    });
  }, [dismissed, jobs]);

  useEffect(() => {
    setSelectedJobId((current) => {
      if (current && visible.some((job) => job.id === current)) return current;
      return visible[0]?.id ?? null;
    });
  }, [catalogueId, visible]);

  if (visible.length === 0) return null;

  const selectedJob = visible.find((job) => job.id === selectedJobId) ?? visible[0];

  return (
    <JobBanner
      job={selectedJob}
      jobs={visible}
      onSelect={setSelectedJobId}
      onDismiss={() => setDismissed((prev) => new Set(prev).add(selectedJob.id))}
    />
  );
}
